/**
 * plantEnumLabels — zentrale deutsche Labels für Pflanzen-Enum-Felder (#751).
 *
 * Einzige Quelle der Wahrheit für die Anzeige der Pflanzen-Enums
 * (sunlight, water_requirement, soil_type, plant_type, pruning_type …).
 * Bodentyp, Wuchstempo, Wuchsform, Wurzeltyp und Pflegeaufwand werden aus
 * plantFilters.js re-exportiert, damit es pro Enum genau EINE Map gibt und die
 * Anzeige nicht mehr in jeder Komponente eine eigene, lückenhafte Kopie führt.
 *
 * translateEnum() ersetzt das frühere stille `|| rohwert`: fehlt ein
 * Schlüssel, gibt es im Dev-Build eine Warnung, statt den englischen Rohwert
 * unbemerkt in die UI durchzureichen (#751 Akzeptanzkriterium).
 */
import {
  SOIL_TYPE_LABELS,
  GROWTH_SPEED_LABELS,
  GROWTH_TYPE_LABELS,
  ROOT_TYPE_LABELS,
  CARE_PRIORITY_LABELS,
} from './plantFilters';

export {
  SOIL_TYPE_LABELS,
  GROWTH_SPEED_LABELS,
  GROWTH_TYPE_LABELS,
  ROOT_TYPE_LABELS,
  CARE_PRIORITY_LABELS,
};

/** Lichtbedarf, Kurzform (Karten, Eigenschaften-Panel). */
export const SUNLIGHT_LABELS = {
  full_sun: 'Volle Sonne',
  'sun-partial_shade': 'Sonne bis Halbschatten',
  partial_sun: 'Teilsonne',
  partial_shade: 'Halbschatten',
  'partial_shade-full_shade': 'Halbschatten bis Schatten',
  full_shade: 'Schatten',
  adaptable: 'Anpassungsfähig',
};

/** Lichtbedarf, Langform mit Stundenangabe (Detaildialog). */
export const SUNLIGHT_LABELS_DETAILED = {
  full_sun: 'Volle Sonne (6+ Stunden direktes Sonnenlicht)',
  'sun-partial_shade': 'Sonne bis Halbschatten (4-6 Stunden)',
  partial_sun: 'Teilsonne (4-6 Stunden)',
  partial_shade: 'Halbschatten (2-4 Stunden)',
  'partial_shade-full_shade': 'Halbschatten bis Schatten',
  full_shade: 'Schatten (<2 Stunden)',
  adaptable: 'Anpassungsfähig',
};

/** Wasserbedarf, Kurzform. */
export const WATER_REQUIREMENT_LABELS = {
  low: 'Niedrig',
  medium: 'Mittel',
  high: 'Hoch',
  very_high: 'Sehr hoch',
};

/** Wasserbedarf, Langform mit Gießhäufigkeit (Detaildialog). */
export const WATER_REQUIREMENT_LABELS_DETAILED = {
  low: 'Niedrig (1-2× pro Monat)',
  medium: 'Mittel (1-2× pro Woche)',
  high: 'Hoch (3+× pro Woche)',
  very_high: 'Sehr hoch (täglich)',
};

/** Botanischer Pflanzentyp (plant_type). */
export const PLANT_TYPE_LABELS = {
  tree: 'Baum',
  shrub: 'Strauch',
  perennial: 'Staude',
  annual: 'Einjährig',
  biennial: 'Zweijährig',
  bulb: 'Zwiebelpflanze',
  grass: 'Gras',
  fern: 'Farn',
  climber: 'Kletterpflanze',
  groundcover: 'Bodendecker',
};

/** Schnittart (care_pruning_type). `none` bewusst null → keine Zeile. */
export const PRUNING_TYPE_LABELS = {
  none: null,
  light_form_pruning: 'Leichter Formschnitt',
  thinning_out: 'Auslichten',
  radical_cut_back: 'Radikaler Rückschnitt',
  deadheading_only: 'Nur Verblühtes entfernen',
  cut_back_after_flowering: 'Nach der Blüte zurückschneiden',
  cleaning_only: 'Nur abgestorbene Teile entfernen',
  mowing: 'Mähen',
  heavy_pruning: 'Starker Rückschnitt',
  pinching: 'Entspitzen',
  anytime_if_needed: 'Bei Bedarf jederzeit',
  rejuvenation: 'Verjüngungsschnitt',
};

/**
 * Übersetzt einen einzelnen Enum-Wert.
 *
 * - leerer Wert (null/undefined/'') → null (Aufrufer blendet die Zeile aus)
 * - bekannter Schlüssel → deutsches Label (kann bewusst null sein, z. B. 'none')
 * - unbekannter Schlüssel → null + Dev-Warnung. Bewusst KEIN Durchreichen des
 *   englischen Rohwerts: der Aufrufer setzt einen deutschen Fallback
 *   ('Unbekannt'), sodass Nutzer nie einen englischen Enum-Wert sehen (#751),
 *   und die Warnung deckt die Lücke im Dev-Build auf.
 *
 * @param {Record<string, string|null>} map
 * @param {string|null|undefined} value
 * @param {string} field  Feldname für die Warnung
 * @returns {string|null}
 */
export function translateEnum(map, value, field = 'enum') {
  if (value === null || value === undefined || value === '') return null;
  if (Object.prototype.hasOwnProperty.call(map, value)) return map[value];
  if (process.env.NODE_ENV !== 'production') {
    console.warn(
      `[plantEnumLabels] Keine deutsche Übersetzung für ${field}="${value}" — bitte Label ergänzen.`
    );
  }
  return null;
}

/**
 * Übersetzt einen Enum-Wert, der String ODER Liste sein kann, zu einem
 * zusammengesetzten Label. Leere/„none"-Anteile werden herausgefiltert.
 *
 * @returns {string|null} null, wenn nichts anzuzeigen ist
 */
export function translateEnumList(map, value, field = 'enum', separator = ' / ') {
  if (value === null || value === undefined || value === '') return null;
  const values = Array.isArray(value) ? value : [value];
  const labels = values
    .map((v) => translateEnum(map, v, field))
    .filter(Boolean);
  return labels.length > 0 ? labels.join(separator) : null;
}
